import { HubConnectionState } from "@microsoft/signalr";
import moment from "moment";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { foLiveCheckinApi } from "../../api/foLiveCheckinApi";
import { useHubCheckinContext } from "../../contexts/HubCheckinProvider";
import { ePageBaseStatus } from '../../models/ePageBaseStatus';
import { useLocation, useParams } from "react-router-dom";
type LiveCheckinDataContextType = {
    children: React.ReactNode
}

type LiveCheckinDataContextStoreType = {
    status: ePageBaseStatus,
    dangKyAnHocSinhs: any[],
    filteredData: any[],
    ngayAn?: Date,
    caAnId?: number,
    coSoId?: number,
    tenCaAn?: string,
    tenCoSo?: string
}
export interface IFaceIdMessage {
    keycode: string
    date: string
    personTitle: string
    temp: number
    data: string
    action_type: string
    detected_image_url: string
    placeID: string
    deviceID: string
    deviceName: string
    personName: string
    aliasID: string
    data_type: string
    personID: string
    id: string
    time: number
    personType: string
    placeName: string
    hash: string
    mask: string
}

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const LiveCheckinDataContext = createContext({} as LiveCheckinDataContextStoreType);
const useLiveCheckinDataContext = () => useContext(LiveCheckinDataContext);
const LiveCheckinDataContextProvider = ({ children }: LiveCheckinDataContextType) => {
    const query = useQuery();
    const [coSoId, setCoSoId] = useState<number>(0);
    const [tenCoSo, setTenCoSo] = useState('');
    const [caAnId, setCaAnId] = useState(0);
    const [tenCaAn, setTenCaAn] = useState('');

    useEffect(() => {
        const id = query.get("coso");
        if (id) {
            setCoSoId(Number(id));
        }
    }, [query]);


    const coSoMapping: Record<number, string> = {
        2: "EDISON ECOPARK",
        3: "EDISON AN KHÁNH"
    };

    const mealMapping: Record<number, Record<string, { id: number, name: string }>> = {
        2: {
            "bữa sáng": { id: 14, name: "bữa sáng" },
            "bữa trưa": { id: 15, name: "bữa trưa" },
            "bữa xế": { id: 16, name: "bữa xế" },
        },
        3: {
            "bữa sáng": { id: 9, name: "bữa sáng" },
            "bữa trưa": { id: 10, name: "bữa trưa" },
            "bữa xế": { id: 11, name: "bữa xế" },
        }
    };

    useEffect(() => {
        const now = new Date();
        const hour = now.getHours();
        const minute = now.getMinutes();

        let tenCaAn = "";

        // Điều kiện cho bữa sáng (< 10h25)
        if (hour < 10 || (hour === 10 && minute < 25)) {
            tenCaAn = "bữa sáng";
        }
        // Điều kiện cho bữa trưa (>= 10h25 và < 13h25)
        else if (
            (hour > 10 || (hour === 10 && minute >= 25)) &&
            (hour < 13 || (hour === 13 && minute < 25))
        ) {
            tenCaAn = "bữa trưa";
        }
        // Còn lại là bữa xế (>= 13h25)
        else {
            tenCaAn = "bữa xế";
        }


        const coSoMeals = mealMapping[coSoId];
        const coSoName = coSoMapping[coSoId] || "KXĐ";

        if (coSoMeals && coSoMeals[tenCaAn]) {
            setCaAnId(coSoMeals[tenCaAn].id);
            setTenCaAn(tenCaAn);
        }

        setTenCoSo(coSoName);

        // --- Tính thời điểm reload ---
        const today = now.toDateString();
        const currentTime = now.getTime();
        const targets = [
            new Date(`${today} 06:15:00`).getTime(),
            new Date(`${today} 10:40:00`).getTime(),
            new Date(`${today} 13:40:00`).getTime()
        ];

        const nextTarget = targets.find(t => t > currentTime);

        if (nextTarget) {
            const delay = nextTarget - currentTime;
            const timer = setTimeout(() => {
                window.location.reload();
            }, delay);

            return () => clearTimeout(timer);
        }
    }, [coSoId]);

    const [ngayAn, setNgayAn] = useState<Date>(new Date());
    const [status, setStatus] = useState<ePageBaseStatus>(ePageBaseStatus.is_not_initialization);
    const [renderKey, setRenderKey] = useState("");
    const _dangKyAnHocSinhsRef = useRef<any[]>();
    const { _connectionServer, _isConnected } = useHubCheckinContext();

    const dangKyAnHocSinhs = useMemo(() => {
        return [...(_dangKyAnHocSinhsRef.current ?? [])];
    }, [renderKey])
    const filteredData = useMemo(() => {
        return [...dangKyAnHocSinhs].sort((a, b) => {
            if (!a.gio_diem_danh && !b.gio_diem_danh) return 0;
            if (!a.gio_diem_danh) return 1;
            if (!b.gio_diem_danh) return -1;
            const toSeconds = (time: string) => {
                const [h, m, s] = time.split(":").map(Number);
                return h * 3600 + m * 60 + s;
            };

            return toSeconds(b.gio_diem_danh) - toSeconds(a.gio_diem_danh);
        });
    }, [dangKyAnHocSinhs])
    const store: LiveCheckinDataContextStoreType = {
        status: status,
        dangKyAnHocSinhs: dangKyAnHocSinhs,
        filteredData,
        tenCaAn,
        tenCoSo
    }

    const handleGetDangKyAnHocSinhAsync = useCallback(async () => {
        if (caAnId > 0 && coSoId > 0) {
            setStatus(ePageBaseStatus.is_loading)
            const res = await foLiveCheckinApi.SelectHocSinhAsync({
                dm_coso_id: coSoId,
                ngay_an: moment(ngayAn).format("YYYY-MM-DD"),
                dm_caan_id: caAnId
            })
            if (res.is_success) {
                setRenderKey(uuid())
                _dangKyAnHocSinhsRef.current = res.data.map((x: any) => ({
                    ...x,
                    key: x.ts_hocsinh_id
                }))
            } else {
                _dangKyAnHocSinhsRef.current = [];
            }
            setStatus(ePageBaseStatus.is_completed)
        }
    }, [caAnId, coSoId, ngayAn])
    useEffect(() => {
        handleGetDangKyAnHocSinhAsync();
    }, [handleGetDangKyAnHocSinhAsync])
    useEffect(() => {
        if (_connectionServer) {
            if (_connectionServer && _connectionServer.state === HubConnectionState.Connected) {
                _connectionServer.on('CHECKIN', OnProcessChanged);
            }
            return () => {
                _connectionServer.off('CHECKIN', OnProcessChanged);

            };
        }
    }, [_connectionServer, _isConnected])
    const OnProcessChanged = (data: any) => {
        if (_dangKyAnHocSinhsRef.current) {
            const idx = _dangKyAnHocSinhsRef.current.findIndex(x => x.ma_hs === data.aliasID);
            if (idx >= 0) {
                const gio_diem_danh = data.date
                    ? moment(data.date, "YYYY-MM-DD HH:mm:ss").format("HH:mm:ss")
                    : moment().format("HH:mm:ss");

                const obj = {
                    ..._dangKyAnHocSinhsRef.current[idx],
                    gio_diem_danh: gio_diem_danh,
                    image_url: data.detected_image_url,
                    key: `${_dangKyAnHocSinhsRef.current[idx].ma_hs}-${gio_diem_danh}`
                };
                _dangKyAnHocSinhsRef.current[idx] = {
                    ...obj
                }

                setRenderKey(uuid())
            }
        }

    }
    return (
        <LiveCheckinDataContext.Provider value={store}>
            {children}
        </LiveCheckinDataContext.Provider>
    )
}
export {
    LiveCheckinDataContextProvider,
    useLiveCheckinDataContext
};
